import {
  Microphone01,
  PauseCircle,
  PlayCircle,
  Send01,
  XClose,
} from "@untitled-ui/icons-react";
import { useTranslation } from "react-i18next";

import Button from "@/components/forms/Button";
import { cn } from "@/lib/utils";

import { useAudioPlayer } from "../../hooks/useAudioPlayer";
import { type UseVoiceRecorderReturn } from "../../hooks/useVoiceRecorder";
import { formatVoiceDuration } from "../../utils/voice";
import VoiceWaveform from "./VoiceWaveform";

interface VoiceRecorderBarProps {
  recorder: UseVoiceRecorderReturn;
  stacked: boolean;
  onSend: () => void;
  isSending: boolean;
}

function RecordingDot({ visible = true }: { visible?: boolean }) {
  return (
    <span
      className={cn(
        "relative inline-flex size-2 shrink-0 items-center justify-center",
        !visible && "invisible",
      )}
    >
      <span className="bg-error-300 animate-voice-record-pulse absolute size-2 rounded-full" />
      <span className="bg-error-600 relative size-2 rounded-full" />
    </span>
  );
}

export default function VoiceRecorderBar({
  recorder,
  stacked,
  onSend,
  isSending,
}: VoiceRecorderBarProps) {
  const { t } = useTranslation("messenger.user");
  const player = useAudioPlayer(recorder.previewUrl, recorder.previewBuffer);
  const isRecording = recorder.status === "recording";
  const time = formatVoiceDuration(recorder.elapsedMs, true);

  const preview = (
    <button
      type="button"
      onClick={player.toggle}
      disabled={!recorder.previewUrl || isSending}
      aria-label={player.isPlaying ? t("Pause") : t("Play")}
      className={cn(
        "shrink-0 disabled:opacity-40",
        player.isPlaying
          ? "text-primary-700 hover:text-primary-700"
          : stacked
            ? "hover:text-primary-700 text-gray-600"
            : "hover:text-primary-700 text-gray-700",
      )}
    >
      {player.isPlaying ? (
        <PauseCircle className="size-5" />
      ) : (
        <PlayCircle className="size-5" />
      )}
    </button>
  );

  const recordToggle = (
    <Button
      color={stacked ? "tertiary-gray" : "secondary-gray"}
      square
      size="lg"
      className="shrink-0"
      onClick={isRecording ? recorder.pause : recorder.resume}
      disabled={isSending || (!isRecording && !recorder.canResume)}
    >
      {isRecording ? (
        <PauseCircle
          className={cn("size-5", stacked ? "text-gray-600" : "text-gray-700")}
        />
      ) : (
        <Microphone01
          className={cn("size-5", stacked ? "text-gray-600" : "text-gray-700")}
        />
      )}
    </Button>
  );

  const sendButton = (
    <Button
      color="primary"
      square
      size="lg"
      className="shrink-0"
      onClick={onSend}
      isLoading={isSending}
    >
      <Send01 className="size-5 rtl:rotate-y-180" />
    </Button>
  );

  const timer = (
    <span
      className={cn(
        "voice-time inline-block w-20 text-start font-semibold",
        stacked ? "text-sm text-gray-900" : "text-base text-gray-700",
      )}
    >
      {time}
    </span>
  );

  if (!stacked) {
    return (
      <div className="flex items-center gap-x-2">
        <Button
          color="secondary-gray"
          square
          size="lg"
          className="shrink-0"
          onClick={recorder.reset}
          disabled={isSending}
        >
          <XClose className="size-5 text-gray-700" />
        </Button>

        <div className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white shadow-xs">
          <div className="flex min-h-[calc(2.75rem+2px)] items-center px-3.5">
            {isRecording ? (
              <div className="flex min-w-26.5 items-center gap-2">
                <RecordingDot />
                {timer}
              </div>
            ) : (
              <div className="flex min-w-0 flex-1 items-center gap-2 rtl:flex-row-reverse">
                {preview}
                <VoiceWaveform
                  peaks={player.peaks}
                  progress={player.progress}
                  onSeek={recorder.previewUrl ? player.seek : undefined}
                />
                <div className="flex min-w-26.5 shrink-0 items-center gap-2">
                  <RecordingDot visible={false} />
                  <span className="voice-time text-base font-semibold text-gray-700">
                    {time}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {recordToggle}
        {sendButton}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-x-2">
        <div className="flex min-h-[calc(2.75rem+2px)] min-w-0 flex-1 items-center">
          {isRecording ? (
            <div className="flex flex-1 items-center justify-center gap-2">
              <RecordingDot />
              {timer}
            </div>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-1 rtl:flex-row-reverse">
              {preview}
              <VoiceWaveform
                peaks={player.peaks}
                progress={player.progress}
                onSeek={recorder.previewUrl ? player.seek : undefined}
              />
              <span className="voice-time min-w-16 shrink-0 text-sm font-semibold text-gray-700">
                {time}
              </span>
            </div>
          )}
        </div>
        {recordToggle}
      </div>

      <div className="flex items-center gap-x-2">
        <Button
          color="secondary-gray"
          size="lg"
          className="min-w-0 flex-1 py-3"
          onClick={recorder.reset}
          disabled={isSending}
          startIcon={<XClose className="size-5 text-gray-700" />}
        >
          {t("Cancel", { ns: "core.shared" })}
        </Button>
        {sendButton}
      </div>
    </div>
  );
}
